import { useLocalStore } from 'mobx-react-lite';
import { ChangeEvent, MutableRefObject, useLayoutEffect, useRef } from 'react';

import Tasks from 'APP/Tasks';
import { SendFeedbackProblemEnum } from 'APP/constants/feedback';
import { FeedbackIssueType } from 'APP/main/Popups/DetailedFeedback/DetailedFeedback.constants';
import Entities from 'STORE';

export interface IDetailedFeedbackPresenter {
  isShown: boolean;
  activeIssueType?: FeedbackIssueType;
  activeProblemType?: SendFeedbackProblemEnum;
  inputRef: MutableRefObject<HTMLTextAreaElement | null>;
  descriptionValue: string;
  channelId: string | null;
  starsRate: number | null;
  resetLocalValues(): void;
  onSend(): void;
  onDescriptionChange(event: ChangeEvent<HTMLTextAreaElement>): void;
  onClose(): void;
  onProblemTypeButtonClick(problemType: SendFeedbackProblemEnum): void;
  onIssueTypeButtonClick(issueType: FeedbackIssueType): void;
}

export const useDetailedFeedbackPresenter = (): IDetailedFeedbackPresenter => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const presenter = useLocalStore<IDetailedFeedbackPresenter>(() => ({
    activeIssueType: undefined,
    activeProblemType: undefined,
    descriptionValue: '',

    get isShown(): boolean {
      return Entities.feedback.isDescriptionShown;
    },

    get inputRef(): MutableRefObject<HTMLTextAreaElement | null> {
      return inputRef;
    },

    resetLocalValues: (): void => {
      presenter.activeIssueType = undefined;
      presenter.activeProblemType = undefined;
      presenter.descriptionValue = '';
    },

    onClose: (): void => {
      Entities.feedback.reset();
      presenter.resetLocalValues();
    },

    onDescriptionChange(event: ChangeEvent<HTMLTextAreaElement>): void {
      presenter.descriptionValue = event.target.value;
    },

    onIssueTypeButtonClick: (issueType): void => {
      if (presenter.activeIssueType === issueType) {
        presenter.activeIssueType = undefined;
      } else {
        presenter.activeIssueType = issueType;
      }
      presenter.activeProblemType = undefined;
    },

    onProblemTypeButtonClick: (problemType): void => {
      if (presenter.activeProblemType === problemType) {
        presenter.activeProblemType = undefined;
      } else {
        presenter.activeProblemType = problemType;
      }
    },

    get channelId(): string | null {
      return Entities.feedback.channelId;
    },

    get starsRate(): number | null {
      return Entities.feedback.starsRate;
    },

    onSend: (): void => {
      if (presenter.starsRate && presenter.channelId) {
        Tasks.feedback.sendFeedback({
          rating: String(presenter.starsRate),
          channelId: presenter.channelId,
          problem: presenter.activeProblemType,
          problemDescription: presenter.descriptionValue,
        });
      }

      Entities.feedback.reset();
      presenter.resetLocalValues();
    },
  }));

  // TODO: Copy-past from MediaMessage/presenter/index. Move to MultiLineField
  useLayoutEffect(() => {
    if (presenter.inputRef.current) {
      const ref = presenter.inputRef.current;
      const len = ref.value.length;
      ref.focus();
      ref.setSelectionRange(len, len);
    }
  }, []);

  useLayoutEffect(() => {
    const ref = presenter.inputRef.current;
    if (ref) {
      ref.style.height = 'auto';
      ref.style.height = `${ref.scrollHeight}px`;
    }
  }, [presenter.descriptionValue]);

  return presenter;
};
